/**
 * Whole rupees, grouped the Indian way.
 *
 * Sits beside `formatPrice`'s output on the same card, and an ungrouped
 * "₹80000 per class" next to "₹4,00,000.00" reads as a different currency.
 */
const rupees = (major: number): string =>
  new Intl.NumberFormat("en-IN", { maximumFractionDigits: 0 }).format(
    Math.round(major),
  );

/**
 * What one booking costs out of this pack, in minor units.
 *
 * This is the one number about a pack that is true everywhere, because it is
 * derived from the pack alone: price ÷ the bookings it buys. Unlike a saving,
 * it needs nothing from the catalogue and cannot be wrong about a class it was
 * never measured against.
 *
 * ── Bonus credits are not bookings you are buying ────────────────────────────
 *
 * A batch pass carries a credit for something extra — Salsa Nova's monthly
 * Practice & Makeup session — which is optional, occasional, and usually not
 * even scheduled when the pack goes on sale. Dividing the price by every credit
 * counted that bonus as a fifth class and quoted ₹720 for a cycle that is
 * actually ₹900 a class: it undersells the classes and oversells the bonus, and
 * a member reading "5 bookings" reasonably expects five interchangeable ones.
 *
 * So the divisor is the CYCLE, when the pack declares one.
 *
 * Null when there is no single answer — a window pack meters attendance rather
 * than bookings, and scopes that disagree on credit cost have no one figure.
 */
export function perBookingMinor(pack: {
  kind: "credit" | "window";
  priceAmountMinor: number;
  creditCount: number | null;
  creditsPerBooking?: number | null;
  /** Classes in one cycle, for a batch pack. Excludes any bonus credit. */
  classesPerCycle?: number | null;
}): number | null {
  if (pack.kind !== "credit") return null;
  if (pack.priceAmountMinor <= 0) return null;
  if (pack.creditsPerBooking === null) return null;

  const perBooking = Math.max(1, pack.creditsPerBooking ?? 1);
  const billable = pack.classesPerCycle ?? pack.creditCount ?? 0;
  const bookings = Math.floor(Math.max(1, billable) / perBooking);
  if (bookings < 1) return null;

  return Math.round(pack.priceAmountMinor / bookings);
}

export type PackSaving = {
  /** What the booking costs out of the pack. */
  packPriceMinor: number;
  /** What the same booking costs without one. */
  listPriceMinor: number;
  savedMinor: number;
  percent: number;
};

/**
 * The before-and-after for one covered thing, at a price we actually know.
 *
 * This is the whole answer to the mixed-price problem. A pack covering classes
 * at ₹600, ₹800 and ₹1,200 does not have *a* saving — it has one per class —
 * so the comparison is made where the class is known and refused where it is
 * not. Returns null when there is no saving to advertise, which keeps the
 * "costs more than the drop-in" case out of buyer-facing surfaces by
 * construction rather than by every caller remembering to filter it.
 */
export function savingAgainst(
  packPriceMinor: number | null,
  listPriceMinor: number | null | undefined,
): PackSaving | null {
  if (packPriceMinor == null || packPriceMinor <= 0) return null;
  if (listPriceMinor == null || listPriceMinor <= 0) return null;

  const savedMinor = listPriceMinor - packPriceMinor;
  if (savedMinor <= 0) return null;

  return {
    packPriceMinor,
    listPriceMinor,
    savedMinor,
    percent: Math.round((savedMinor / listPriceMinor) * 100),
  };
}

/**
 * What a pack is worth to the buyer, computed as the queen types.
 *
 * Authoring-side only. It is measured against the single `unitReference` the
 * queen enters, which is the refund anchor — so it answers "is the pack I am
 * writing priced sensibly against the class I have in mind", not "what does
 * this buyer save". Buyer-facing surfaces use `savingAgainst` with a price
 * they actually know.
 *
 * Lives apart from the form component so it can be tested without dragging in
 * the server actions that component imports.
 */
export function savingsLabel(values: {
  kind: "credit" | "window";
  priceMajor: number;
  unitReferenceMajor: number;
  creditCount: number;
  /**
   * Credits one booking costs, when every scope on the pack agrees.
   *
   * Defaults to 1, which is what the figure silently assumed before: a pack
   * whose scope charges 2 credits a booking was advertised at half its real
   * per-class price, so an ₹8,000 pack of 8 credits read as "₹1,000 per class"
   * when a class actually costs ₹2,000 of it. Null when scopes disagree, and
   * there is then no single per-class price to quote.
   */
  creditsPerBooking?: number | null;
}): string | null {
  // A window pack has no per-class price to compare against.
  if (values.kind !== "credit") return null;
  if (values.priceMajor <= 0 || values.unitReferenceMajor <= 0) return null;
  // Scopes disagree: this pack has no one per-class price.
  if (values.creditsPerBooking === null) return null;

  const perBooking = Math.max(1, values.creditsPerBooking ?? 1);
  const bookings = Math.floor(Math.max(1, values.creditCount) / perBooking);
  if (bookings < 1) return null;

  const perCredit = values.priceMajor / bookings;
  const saved = values.unitReferenceMajor - perCredit;

  // The mistake worth catching as it is made: a pack priced at or above the
  // drop-in rate is a worse deal than paying each time, so nobody buys it and
  // the queen never finds out why.
  if (saved <= 0) {
    return `₹${rupees(perCredit)} per class — more than the ₹${rupees(values.unitReferenceMajor)} drop-in price.`;
  }

  const pct = Math.round((saved / values.unitReferenceMajor) * 100);
  return `₹${rupees(perCredit)} per class · members save ${pct}%`;
}
